// Which backend the report dashboard talks to.
//
// The tabs fetch their own data, several of them from inside components a thousand lines long.
// Threading a subject prop down to each call site would mean touching every layer in between for
// a value none of them care about, so the fetchers are supplied by context instead: the page
// chooses a set once, and a tab just asks for "the fetchers".
//
// The default is the MATHS set, so a component that has not been wrapped behaves exactly as it
// did before this existed. That is deliberate - it means this can be adopted one tab at a time,
// and a tab nobody has converted is not silently broken.

import React from "react"
import {
  getStudentSkillsGap,
  getStudentPrereqGraph,
  getStudentTopicAttemptDetail,
} from "./bmd-report.service"
import type {
  GetStudentSkillsGapRequest,
  GetStudentSkillsGapResponse,
  GetStudentPrereqGraphRequest,
  GetStudentPrereqGraphResponse,
  GetTopicAttemptDetailRequest,
  GetTopicAttemptDetailResponse,
} from "../types/bmd-report.types"

export type ReportFetchers = {
  getStudentSkillsGap: (
    req: GetStudentSkillsGapRequest,
    signal?: AbortSignal,
  ) => Promise<GetStudentSkillsGapResponse>
  getStudentPrereqGraph: (
    req: GetStudentPrereqGraphRequest,
    signal?: AbortSignal,
  ) => Promise<GetStudentPrereqGraphResponse>
  /**
   * One topic block, question by question. Used by the Chain Analysis tab, which fans one of
   * these out per topic. The English side folds its per-part verdicts into one per question on
   * the way through - see ENG_REPORT_FETCHERS - because that tab asks only whether the base
   * question was wrong.
   */
  getStudentTopicAttemptDetail: (
    req: GetTopicAttemptDetailRequest,
    signal?: AbortSignal,
  ) => Promise<GetTopicAttemptDetailResponse>
}

const MATH_FETCHERS: ReportFetchers = {
  getStudentSkillsGap,
  getStudentPrereqGraph,
  getStudentTopicAttemptDetail,
}

const ReportFetchersContext = React.createContext<ReportFetchers>(MATH_FETCHERS)

export const ReportFetchersProvider: React.FC<{
  value: ReportFetchers
  children: React.ReactNode
}> = ({ value, children }) => (
  <ReportFetchersContext.Provider value={value}>
    {children}
  </ReportFetchersContext.Provider>
)

/** The fetchers for whichever subject is on screen. Maths unless a provider says otherwise. */
export const useReportFetchers = (): ReportFetchers =>
  React.useContext(ReportFetchersContext)

export { MATH_FETCHERS }
